// Release addresses… of a retained deployment (spec 11 6.15.14, 6.15.15): a confirmation, then
// `POST /deployments/{id}/network/release`.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { queryKeys } from '../../api/queries';
import type { Deployment } from '../../api/types';
import { useToast } from '../../components/toast';
import { ActionButton, Button, Dialog } from '../../components/ui';
import { strings } from '../../strings';

const s = strings.networks.deployment;

export function ReleaseAddressesButton({
  deployment,
  disabledReason = null,
  onReleased,
}: {
  deployment: Deployment;
  disabledReason?: string | null;
  onReleased?: (deployment: Versioned<Deployment>) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const release = async () => {
    setBusy(true);
    try {
      const updated = await api.releaseDeploymentNetwork(deployment.id);
      queryClient.setQueryData(queryKeys.deployment(deployment.id), updated);
      void queryClient.invalidateQueries({ queryKey: ['deployment', deployment.id] });
      void queryClient.invalidateQueries({ queryKey: ['network'] });
      toast.show({ tone: 'success', title: s.released(deployment.name) });
      onReleased?.(updated);
      setOpen(false);
    } catch (error) {
      toast.error(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <ActionButton
        permission="operate"
        className="btn-sm"
        disabledReason={disabledReason}
        onClick={() => setOpen(true)}
      >
        {s.release}
      </ActionButton>
      {open && (
        <Dialog
          title={s.releaseTitle(deployment.name)}
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button onClick={() => setOpen(false)}>{strings.common.cancel}</Button>
              <Button variant="danger" disabled={busy} onClick={() => void release()}>
                {s.releaseConfirm}
              </Button>
            </>
          }
        >
          <p data-autofocus tabIndex={-1}>
            {s.releaseText(deployment.name)}
          </p>
        </Dialog>
      )}
    </>
  );
}
